import { type FormEvent, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type { FetcherWithComponents } from '@remix-run/react';
import { DEFAULT_GROUP_MIN_QUANTITY } from '@shared/dream-components';
import { useElements } from '@stripe/react-stripe-js';

import { useUpgradeContext } from '~/context/useUpgradeContext';
import useGiftOrGroupCheckout from '~/pages/upgrade/hooks/useGiftOrGroupCheckout';
import useInternalCheckout from '~/pages/upgrade/hooks/useInternalCheckout';
import { validateGroupSubmission } from '~/pages/upgrade/utils/validateGroupSubmission';

interface Params {
  fetcher: FetcherWithComponents<unknown>;
  setIsSubmitting: (value: boolean) => void;
  onSuccess: (redirectToSSOLogin: boolean) => void;
  usePaymentElement?: boolean;
}

const useGroupCheckoutSubmitHandler = ({ fetcher, setIsSubmitting, onSuccess, usePaymentElement }: Params) => {
  const { t, i18n } = useTranslation();
  const { isInternalCheckout, stripeSession, selectedGroupTier, selectedGroupPrice, email, seatCount, groupName } =
    useUpgradeContext();
  const elements = useElements();
  const { handleSubmit } = useGiftOrGroupCheckout(isInternalCheckout, t('upgrade.error.card_processing_failed'));
  const { handleSubmitWithPaymentElement } = useInternalCheckout();

  return useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);

      const minimumQuantity = selectedGroupTier?.group_minimum_quantity ?? DEFAULT_GROUP_MIN_QUANTITY;
      const validation = validateGroupSubmission({
        email,
        groupName,
        seatCount,
        minimumQuantity,
        isInternalCheckout,
        hasStripeSession: !!stripeSession,
        hasElements: !!elements,
        hasSelectedGroupPrice: !!selectedGroupPrice,
        messages: {
          missingEmail: t('upgrade.error.email_required'),
          missingGroupName: t('upgrade.tabs.group.error.group_name_required'),
          belowMinimumSeats: t('upgrade.tabs.group.error.minimum_seats', {
            count: minimumQuantity,
            formattedCount: new Intl.NumberFormat(i18n.language).format(minimumQuantity),
          }),
          stripeNotReady: t('upgrade.error.stripe_not_ready'),
          selectPrice: t('upgrade.error.select_price'),
        },
      });

      if (!validation.ok) {
        toast.error(validation.message);
        setIsSubmitting(false);
        return;
      }

      // Narrow nullable values for TypeScript. validateGroupSubmission already
      // guarantees these are present, but the type system can't carry that proof.
      if (!email) {
        setIsSubmitting(false);
        return;
      }

      try {
        if (usePaymentElement && isInternalCheckout) {
          if (!stripeSession || !elements) {
            setIsSubmitting(false);
            return;
          }

          await handleSubmitWithPaymentElement({
            event,
            stripeSession,
            elements,
            email,
            defaultErrorMessage: t('errors.default'),
          });

          onSuccess(false);
        } else {
          await handleSubmit(event, fetcher, stripeSession, elements, email);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : t('upgrade.error.payment_request_failed');
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      t,
      email,
      isInternalCheckout,
      stripeSession,
      elements,
      selectedGroupTier,
      selectedGroupPrice,
      seatCount,
      groupName,
      usePaymentElement,
      fetcher,
      setIsSubmitting,
      onSuccess,
      handleSubmit,
      handleSubmitWithPaymentElement,
      i18n.language,
    ],
  );
};

export default useGroupCheckoutSubmitHandler;
